import * as React from "react";
import { Button, Checkbox, CheckboxProps, Form, FormField, Modal, Radio } from "semantic-ui-react";

import { LinkObject } from "@teamprevent/common/model";

import { OhCaseDocument, OhCaseDocumentVisibilityOptions } from "../../model";

import { OhCaseDocumentApi } from "../../ohCaseDocumentApi";

interface DocumentVisibilitySettingsModalProps {
    modalOpen: boolean;
    ohCaseId: string;
    customerId: string;
    ohCaseDocument: OhCaseDocument | undefined;
    onDocumentVisibilityChange: (selectedVisibility: keyof typeof OhCaseDocumentVisibilityOptions) => void;
    onCloseVisibilitySettingsModal: () => void;
    genericCaseType: LinkObject | null;
}

const mapOhCaseDocumentVisibilityRadioOptions = (documentType: string): string => {
    switch (documentType) {
        case OhCaseDocumentVisibilityOptions.Employee:
            return "Employee Portal";
        case OhCaseDocumentVisibilityOptions.Manager:
            return "Manager Portal";
        case OhCaseDocumentVisibilityOptions.EmployeeAndManager:
            return "Both Portals";
        default:
            return "";
    }
};

export const DocumentVisibilitySettingsModal: React.FunctionComponent<DocumentVisibilitySettingsModalProps> = (
    {
        modalOpen,
        customerId,
        ohCaseDocument,
        ohCaseId,
        onCloseVisibilitySettingsModal,
        onDocumentVisibilityChange,
        genericCaseType
    }) => {
    const [selectedVisibility, setSelectedVisibility] = React.useState<OhCaseDocumentVisibilityOptions | undefined>(undefined);
    const [sendEmailNotifications, setSendEmailNotifications] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (ohCaseDocument) {
            setSelectedVisibility(ohCaseDocument.visibleTo as OhCaseDocumentVisibilityOptions);
        }
    }, [ohCaseDocument]);

    const closeModal = React.useCallback(() => {
        onCloseVisibilitySettingsModal();
        setSelectedVisibility(undefined);
        setSendEmailNotifications(false);
    }, [onCloseVisibilitySettingsModal]);

    const handleVisibilitySettingsChange = React.useCallback((_event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => {
        setSelectedVisibility(data.value as OhCaseDocumentVisibilityOptions);
    }, []);

    const handleSendEmailNotificationsChange = React.useCallback((_event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => {
        setSendEmailNotifications(data.checked ?? false);
    }, []);

    const submitVisibilitySettings = React.useCallback(async () => {
        if (!ohCaseDocument || !selectedVisibility || selectedVisibility === ohCaseDocument.visibleTo) {
            onCloseVisibilitySettingsModal();
            return;
        }

        await OhCaseDocumentApi.updateVisibilitySetting(customerId, ohCaseId, ohCaseDocument.id, selectedVisibility, sendEmailNotifications);

        onDocumentVisibilityChange(selectedVisibility);
        setSelectedVisibility(undefined);
        onCloseVisibilitySettingsModal();
    }, [ohCaseDocument, selectedVisibility, customerId, ohCaseId, sendEmailNotifications, onDocumentVisibilityChange, onCloseVisibilitySettingsModal]);

    if (!ohCaseDocument)
    {
        return null;
    }

    return (
        <Modal open={modalOpen} size="tiny">
            <Modal.Header>
                Visible To
            </Modal.Header>
            <Modal.Content>
                <Form>
                    <FormField>
                        <Radio
                            label={mapOhCaseDocumentVisibilityRadioOptions(OhCaseDocumentVisibilityOptions.Manager)}
                            name="ohCaseVisibilitySettings"
                            value={OhCaseDocumentVisibilityOptions.Manager}
                            checked={selectedVisibility === OhCaseDocumentVisibilityOptions.Manager}
                            onChange={handleVisibilitySettingsChange}
                        />
                    </FormField>
                    <FormField>
                        <Radio
                            label={mapOhCaseDocumentVisibilityRadioOptions(OhCaseDocumentVisibilityOptions.Employee)}
                            name="ohCaseVisibilitySettings"
                            value={OhCaseDocumentVisibilityOptions.Employee}
                            checked={selectedVisibility === OhCaseDocumentVisibilityOptions.Employee}
                            onChange={handleVisibilitySettingsChange}
                        />
                    </FormField>
                    <FormField>
                        <Radio
                            label={mapOhCaseDocumentVisibilityRadioOptions(OhCaseDocumentVisibilityOptions.EmployeeAndManager)}
                            name="ohCaseVisibilitySettings"
                            value={OhCaseDocumentVisibilityOptions.EmployeeAndManager}
                            checked={selectedVisibility === OhCaseDocumentVisibilityOptions.EmployeeAndManager}
                            onChange={handleVisibilitySettingsChange}
                        />
                    </FormField>
                    <div>
                        If this document should not be visible in either of the customer portals,
                        please delete and upload using either the Clinical Notes or General document types.
                    </div>
                    <br />
                    {genericCaseType && <FormField>
                        <Checkbox
                            label="Send update email to user(s) selected above"
                            name="sendEmailNotifications"
                            checked={sendEmailNotifications}
                            onChange={handleSendEmailNotificationsChange} />
                    </FormField>}
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    content="Cancel"
                    onClick={closeModal}
                />
                <Button
                    content="Save"
                    className="green"
                    onClick={submitVisibilitySettings}
                />
            </Modal.Actions>
        </Modal>
    );
};
